import {
  VenueDealStatusActive,
  VenueDealStatusArchived,
  VenueDealStatusPaused,
  VenueWeekdayFriday,
  VenueWeekdayMonday,
  VenueWeekdaySaturday,
  VenueWeekdaySunday,
  VenueWeekdayThursday,
  VenueWeekdayTuesday,
  VenueWeekdayWednesday,
  type VenueDealStatus,
  type VenuePublicDeal,
  type VenueWeekday,
} from '@/lib/api/apiTypes';

export type DealTiming = {
  label: string;
  minutesUntil: number;
  state: 'archived' | 'live' | 'paused' | 'scheduled';
};

export const weekdayOptions: { label: string; short: string; value: VenueWeekday }[] = [
  { label: 'Monday', short: 'Mon', value: VenueWeekdayMonday },
  { label: 'Tuesday', short: 'Tue', value: VenueWeekdayTuesday },
  { label: 'Wednesday', short: 'Wed', value: VenueWeekdayWednesday },
  { label: 'Thursday', short: 'Thu', value: VenueWeekdayThursday },
  { label: 'Friday', short: 'Fri', value: VenueWeekdayFriday },
  { label: 'Saturday', short: 'Sat', value: VenueWeekdaySaturday },
  { label: 'Sunday', short: 'Sun', value: VenueWeekdaySunday },
];

const weekdayByDateIndex: VenueWeekday[] = [
  VenueWeekdaySunday,
  VenueWeekdayMonday,
  VenueWeekdayTuesday,
  VenueWeekdayWednesday,
  VenueWeekdayThursday,
  VenueWeekdayFriday,
  VenueWeekdaySaturday,
];

export function getDealTiming(
  deal: Pick<
    VenuePublicDeal,
    'active_days' | 'ends_at_local' | 'skipped_dates' | 'starts_at_local' | 'status'
  >,
  now = new Date()
): DealTiming {
  if (deal.status === VenueDealStatusArchived) {
    return { label: 'Archived', minutesUntil: 0, state: 'archived' };
  }
  if (deal.status === VenueDealStatusPaused) {
    return { label: 'Paused', minutesUntil: 0, state: 'paused' };
  }

  const startMinutes = timeToMinutes(deal.starts_at_local);
  const endMinutes = timeToMinutes(deal.ends_at_local);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const today = weekdayByDateIndex[now.getDay()];

  if (
    !isSkippedDealDate(deal, now) &&
    deal.active_days.includes(today) &&
    nowMinutes >= startMinutes &&
    nowMinutes < endMinutes
  ) {
    const minutesUntil = endMinutes - nowMinutes;
    return { label: `Ends in ${formatDuration(minutesUntil, 'end')}`, minutesUntil, state: 'live' };
  }

  const minutesUntil = minutesUntilNextStart(deal, now, nowMinutes, startMinutes);
  return { label: `Next in ${formatDuration(minutesUntil)}`, minutesUntil, state: 'scheduled' };
}

export function sortDealsByTiming<
  T extends Pick<
    VenuePublicDeal,
    'active_days' | 'ends_at_local' | 'skipped_dates' | 'starts_at_local' | 'status'
  >,
>(deals: T[], now = new Date()) {
  return [...deals].sort((left, right) => {
    const leftTiming = getDealTiming(left, now);
    const rightTiming = getDealTiming(right, now);
    const stateRank = { live: 0, scheduled: 1, paused: 2, archived: 3 };
    return (
      stateRank[leftTiming.state] - stateRank[rightTiming.state] ||
      leftTiming.minutesUntil - rightTiming.minutesUntil
    );
  });
}

export function statusLabel(status: VenueDealStatus, timing?: DealTiming) {
  if (status === VenueDealStatusActive && timing?.state === 'live') return 'Live now';
  if (status === VenueDealStatusActive) return 'Scheduled';
  return status;
}

export function formatDealDays(days: VenueWeekday[]) {
  if (days.length === 7) return 'Every day';
  return weekdayOptions
    .filter((day) => days.includes(day.value))
    .map((day) => day.short)
    .join(', ');
}

export function formatDealTimeRange(
  deal: Pick<VenuePublicDeal, 'ends_at_local' | 'starts_at_local'>
) {
  return `${formatDealTime(deal.starts_at_local)}-${formatDealTime(deal.ends_at_local)}`;
}

function minutesUntilNextStart(
  deal: Pick<VenuePublicDeal, 'active_days' | 'skipped_dates'>,
  now: Date,
  nowMinutes: number,
  startMinutes: number
) {
  if (deal.active_days.length === 0) return 0;

  for (let dayOffset = 0; dayOffset < 14; dayOffset += 1) {
    const candidateDate = addDays(now, dayOffset);
    const candidateDay = weekdayByDateIndex[candidateDate.getDay()];
    if (!deal.active_days.includes(candidateDay)) continue;
    if (isSkippedDealDate(deal, candidateDate)) continue;
    if (dayOffset === 0 && nowMinutes < startMinutes) return startMinutes - nowMinutes;
    if (dayOffset > 0) return dayOffset * 24 * 60 - nowMinutes + startMinutes;
  }

  return 7 * 24 * 60 - nowMinutes + startMinutes;
}

function isSkippedDealDate(deal: Pick<VenuePublicDeal, 'skipped_dates'>, date: Date) {
  return deal.skipped_dates.includes(toLocalDateKey(date));
}

function toLocalDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function timeToMinutes(value: string) {
  const [hourValue, minuteValue] = value.split(':');
  return Number(hourValue) * 60 + Number(minuteValue);
}

function formatDuration(minutes: number, mode: 'end' | 'start' = 'start') {
  if (minutes <= 0) return mode === 'end' ? '0m' : 'now';
  if (minutes >= 24 * 60) {
    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes % (24 * 60)) / 60);
    return hours === 0
      ? `${days} ${days === 1 ? 'day' : 'days'}`
      : `${days} ${days === 1 ? 'day' : 'days'} ${hours}h`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes}m`;
  if (mode === 'start' || remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
}

function formatDealTime(value: string) {
  const [hourValue, minuteValue] = value.split(':');
  const hour = Number(hourValue);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${minuteValue} ${suffix}`;
}
